import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5rrfk3592clg457kr4g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-24 bg-white relative">
      <div className="absolute top-0 left-0 right-0 h-px bg-grey-200"></div>
      
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-navy-900 mb-4">
            Get In Touch
          </h2>
          <p className="text-lg text-grey-600 mb-6">
            Tell us about your goals and challenges — we'll provide the right guidance.
          </p>
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2 text-grey-700">
              <i className="ri-phone-line text-navy-600 text-xl"></i>
              <a href="tel:+13313213509" className="font-medium hover:text-navy-700 transition-colors cursor-pointer">
                (331) 321-3509
              </a>
            </div>
            <div className="flex items-center gap-2 text-grey-700">
              <i className="ri-mail-line text-navy-600 text-xl"></i>
              <a href="mailto:info@ironassetmanagement.com" className="font-medium hover:text-navy-700 transition-colors cursor-pointer">
                info@ironassetmanagement.com
              </a>
            </div>
          </div>
          <p className="text-sm text-grey-500 mt-2">
            All inquiries are reviewed within 24–48 business hours.
          </p>
        </div>

        <form 
          id="contact-lead-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-grey-50 rounded-lg p-8 md:p-12 shadow-lg border border-grey-200"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-semibold text-navy-900 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent text-sm bg-white"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-navy-900 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent text-sm bg-white"
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-navy-900 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent text-sm bg-white"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="service" className="block text-sm font-semibold text-navy-900 mb-2">
                Service Category *
              </label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent text-sm cursor-pointer bg-white"
              >
                <option value="">Select a service</option>
                <option value="General Business Services">General Business Services</option>
                <option value="Property Support Services">Property Support Services</option>
                <option value="Automotive Services">Automotive Services</option>
                <option value="Asset Management">Asset Management</option>
                <option value="General Inquiry">General Inquiry</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-semibold text-navy-900 mb-2">
              Message *
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={5}
              maxLength={500}
              className="w-full px-4 py-3 border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent resize-none text-sm bg-white"
              placeholder="Tell us about your goals and challenges..."
            ></textarea>
            <div className="text-right text-xs text-grey-500 mt-1">
              {formData.message.length}/500 characters
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
              <p className="text-green-700 text-sm font-medium">Thank you! Your request has been submitted successfully.</p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <i className="ri-error-warning-line text-red-600 text-xl"></i>
              <p className="text-red-700 text-sm font-medium">Something went wrong. Please try again.</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-8 py-4 bg-navy-700 text-white rounded-lg hover:bg-navy-600 transition-all cursor-pointer font-semibold text-lg shadow-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] transform"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </div>
    </section>
  );
}
